import { useQuery } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { ArrowLeft, Phone, Mail, CheckCircle, ChevronRight, AlertTriangle } from "lucide-react"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"

/**
 * Kunden-Detail — analog zu admin/dashboard.py::admin_dashboard_kunde.
 *
 * 3 Stat-Cards oben (Calls / Mails / Tasks) + pro-Tenant-Tabelle + 3
 * Mini-Cards mit den letzten 5 offenen Items jeder Kategorie.
 */
export default function KundeDetail() {
  const { slug } = useParams<{ slug: string }>()
  const { data, isLoading, error } = useQuery({
    queryKey: ["kunde", slug],
    queryFn: async () => {
      if (!slug) throw new Error("kein slug")
      const { data, error } = await api.GET("/api/v1/kunden/{slug}", {
        params: { path: { slug } },
      })
      if (error) throw new Error(String(error))
      return data
    },
    enabled: !!slug,
  })

  if (isLoading) return <PageMessage text="Lädt Kunden-Detail..." />
  if (error || !data) return <PageMessage text={`Fehler: ${error instanceof Error ? error.message : "unbekannt"}`} error />

  return (
    <div className="px-10 py-8 max-w-[1440px]">
      {/* Breadcrumb + Titel */}
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Kunden-Übersicht
        </Link>
        <h1 className="text-[22px] font-bold">{data.kunden_name}</h1>
        <div className="text-xs text-muted-foreground mt-1">
          {data.tenants.length} Tenant{data.tenants.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Stat-Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard icon={<Phone className="w-5 h-5" />} label="Offene Anrufe" value={data.total_calls} color="#ef4444" href="/sipgate" />
        <StatCard icon={<Mail className="w-5 h-5" />} label="Neue E-Mails" value={data.total_emails} color="#d97706" href="/emails" />
        <StatCard icon={<CheckCircle className="w-5 h-5" />} label="Offene Aufgaben" value={data.total_tasks} color="#4f46e5" href="/tasks" />
      </div>

      {/* Pro-Tenant-Tabelle */}
      <Section title="Tenants">
        <div className="rounded-lg border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary">
                <Th>Tenant</Th>
                <Th className="text-right">Anrufe</Th>
                <Th className="text-right">E-Mails</Th>
                <Th className="text-right">Aufgaben</Th>
              </tr>
            </thead>
            <tbody>
              {data.tenants.map((t) => (
                <tr key={t.tenant_slug} className="border-b last:border-0 hover:bg-secondary/50">
                  <td className="px-4 py-3">
                    <div className="font-medium">{t.tenant_name}</div>
                    <div className="text-xs text-muted-foreground">{t.tenant_slug}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">{t.open_calls}</td>
                  <td className="px-4 py-3 text-right font-medium">{t.open_emails}</td>
                  <td className="px-4 py-3 text-right font-medium">{t.open_tasks}</td>
                </tr>
              ))}
              {data.tenants.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Keine Tenants</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Mini-Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        <MiniCard title="Jüngste Anrufe" color="#ef4444" items={data.recent_calls.map((c) => ({
          id: `c-${c.kind}-${c.id}`,
          title: c.name || c.phone,
          sub: c.status,
          timestamp: c.timestamp,
          urgent: c.urgent,
        }))} empty="Keine offenen Anrufe" href="/sipgate" />
        <MiniCard title="Neue E-Mails" color="#d97706" items={data.recent_emails.map((e) => ({
          id: `e-${e.id}`,
          title: e.subject,
          sub: e.sender,
          timestamp: e.received_at,
          urgent: false,
        }))} empty="Keine neuen E-Mails" href="/emails" />
        <MiniCard title="Aufgaben" color="#4f46e5" items={data.recent_tasks.map((t) => ({
          id: `t-${t.id}`,
          title: t.title,
          sub: t.target_user_name || "—",
          timestamp: t.due_date,
          urgent: false,
        }))} empty="Keine offenen Aufgaben" href="/tasks" />
      </div>
    </div>
  )
}

function PageMessage({ text, error }: { text: string; error?: boolean }) {
  return <div className={cn("p-10", error ? "text-destructive" : "text-muted-foreground")}>{text}</div>
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h3 className="text-[15px] font-semibold mb-3">{title}</h3>
      {children}
    </section>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={cn("px-4 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground", className)}>
      {children}
    </th>
  )
}

function StatCard({ icon, label, value, color, href }: {
  icon: React.ReactNode; label: string; value: number; color: string; href: string;
}) {
  return (
    <Link
      to={href}
      className="block rounded-[var(--radius)] border bg-card p-5 shadow-xs hover:-translate-y-0.5 hover:shadow-lg transition-all no-underline text-foreground"
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <div className="flex items-start justify-between">
        <div style={{ color }}>{icon}</div>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="text-3xl font-bold mt-3">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </Link>
  )
}

function MiniCard({ title, color, items, empty, href }: {
  title: string
  color: string
  items: Array<{ id: string; title: string; sub: string; timestamp: string | null | undefined; urgent: boolean }>
  empty: string
  href: string
}) {
  return (
    <div className="rounded-[var(--radius)] border bg-card shadow-xs overflow-hidden">
      <div className="px-5 py-3 border-b bg-secondary flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-bold">{title}</span>
          {items.length > 0 && (
            <span
              className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[11px] font-bold"
              style={{ background: `color-mix(in srgb, ${color} 15%, transparent)`, color }}
            >
              {items.length}
            </span>
          )}
        </div>
        <Link to={href} className="text-xs text-primary hover:underline">Alle &rarr;</Link>
      </div>
      {items.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">{empty}</div>
      ) : (
        <div>
          {items.map((item) => (
            <div key={item.id} className="px-5 py-2.5 border-b last:border-0 hover:bg-secondary/50 flex items-start gap-3">
              {item.urgent && <AlertTriangle className="w-3.5 h-3.5 mt-1 text-danger shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium truncate">{item.title || "—"}</div>
                <div className="text-[11px] text-muted-foreground truncate">{item.sub}</div>
              </div>
              {item.timestamp && (
                <div className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtShort(item.timestamp)}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function fmtShort(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) +
      " " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
  } catch { return iso.slice(0, 16) }
}
